import { useCallback, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { sidebarWidth } from './layout-tokens';
import {
  NAV_GROUPS,
  availabilityLabel,
  isNavItemActive,
  isNavItemEnabled,
  type NavGroup,
  type NavItem,
} from './navigation';
import { SYSTEM_VERSION_LABEL } from '../version';

export interface SidebarProps {
  readonly expanded: boolean;
  readonly groups?: readonly NavGroup[];
  readonly onToggle: () => void;
}

function itemTitle(item: NavItem): string {
  const suffix = item.note ? ` · ${item.note}` : '';
  return `${item.label} (${availabilityLabel(item.availability)})${suffix}`;
}

/**
 * WO-P2-M4-009 — เมนูย่อยแบบ dropdown (ชั้นที่สามของเมนู) พับไว้เป็นค่าเริ่มต้น
 *
 * สถานะกาง/พับต่อรายการถูกจำไว้ที่ `localStorage` ของเบราว์เซอร์เท่านั้น — เป็น
 * ความสะดวกของหน้าจอ ไม่ใช่ข้อมูลธุรกิจ จึงไม่มีการส่งขึ้นเซิร์ฟเวอร์ อ่าน/เขียนถูก
 * ห่อด้วย try/catch เสมอ: เปิด private window หรือถูกบล็อกต้องไม่ทำให้ Sidebar พัง
 * แค่กลับไปพับตามค่าเริ่มต้น (ไม่มีรายการไหนถูกจำไว้เลย)
 */
const EXPANDED_CHILDREN_STORAGE_KEY = 'cap.sidebar.expandedChildren.v1';

function readStoredExpandedChildren(): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(EXPANDED_CHILDREN_STORAGE_KEY);
    if (raw === null) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, boolean>;
    }
    return {};
  } catch {
    return {};
  }
}

function writeStoredExpandedChildren(map: Record<string, boolean>): void {
  try {
    window.localStorage.setItem(EXPANDED_CHILDREN_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // localStorage พังได้ (private window / ถูกบล็อก) — ปล่อยผ่านเงียบ ๆ ไม่ใช่ error ของหน้าจอ
  }
}

/**
 * รายการนี้มีลูกที่ active อยู่ตอนนี้หรือไม่ — ใช้ตัดสินข้อยกเว้นเดียวของการพับ
 * เป็นค่าเริ่มต้น: เมื่อหน้าปัจจุบันคือลูกตัวใดตัวหนึ่ง ให้กางไว้ ไม่งั้นผู้ใช้จะอยู่
 * ในหน้าที่มองไม่เห็นว่ามันอยู่ตรงไหนในเมนู (พฤติกรรมมาตรฐานของ dropdown)
 */
function hasActiveChild(item: NavItem, pathname: string, search: string): boolean {
  return (item.children ?? []).some((child) => isNavItemActive(child, pathname, search));
}

function NavChildEntry({
  child,
  expanded,
  pathname,
  search,
}: {
  child: NavItem;
  expanded: boolean;
  pathname: string;
  search: string;
}) {
  if (child.to === undefined) {
    return null;
  }
  const active = isNavItemActive(child, pathname, search);
  return (
    <li>
      <Link
        to={child.to}
        className={`cap-nav__item cap-nav__item--child${active ? ' cap-nav__item--active' : ''}`}
        title={itemTitle(child)}
        aria-current={active ? 'page' : undefined}
        data-testid={`nav-child-${child.id}`}
      >
        <span className="cap-nav__icon" aria-hidden="true">
          {child.icon}
        </span>
        {expanded ? <span className="cap-nav__label">{child.label}</span> : null}
        {!expanded ? <span className="cap-visually-hidden">{child.label}</span> : null}
      </Link>
    </li>
  );
}

function NavEntry({
  item,
  expanded,
  expandedChildren,
  onToggleChildren,
}: {
  item: NavItem;
  expanded: boolean;
  expandedChildren: Record<string, boolean>;
  onToggleChildren: (itemId: string, nextValue: boolean) => void;
}) {
  const location = useLocation();

  if (isNavItemEnabled(item) && item.to !== undefined) {
    const active = isNavItemActive(item, location.pathname, location.search);
    // ลูกที่ `hidden: true` (D-131, เช่น `manual-plan`) ไม่นับเป็นลูกที่ต้องแสดง
    // เลย — ไม่มีปุ่มลูกศรให้กด ไม่มี dropdown ให้กาง แม้ข้อมูลใน `navigation.ts`
    // จะยังมี `children` อยู่ก็ตาม
    const visibleChildren = (item.children ?? []).filter((child) => !child.hidden);
    const hasChildren = visibleChildren.length > 0;
    // ค่าเริ่มต้นคือพับ — ยกเว้นตอนนี้อยู่ในหน้าลูกตัวใดตัวหนึ่ง (ข้อยกเว้นเดียว)
    // และผู้ใช้ยังไม่เคยกด toggle รายการนี้มาก่อน (สถานะที่จำไว้ชนะเสมอ ผู้ใช้
    // พับเองได้แม้ขณะอยู่ในหน้าลูก)
    const childrenExpanded = hasChildren
      ? (expandedChildren[item.id] ?? hasActiveChild(item, location.pathname, location.search))
      : false;
    const childrenId = `cap-nav-children-${item.id}`;

    return (
      <li>
        <div className="cap-nav__row">
          <Link
            to={item.to}
            className={`cap-nav__item${active ? ' cap-nav__item--active' : ''}`}
            title={itemTitle(item)}
            aria-current={active ? 'page' : undefined}
          >
            <span className="cap-nav__icon" aria-hidden="true">
              {item.icon}
            </span>
            {expanded ? <span className="cap-nav__label">{item.label}</span> : null}
            {!expanded ? <span className="cap-visually-hidden">{item.label}</span> : null}
          </Link>
          {/*
            ปุ่มลูกศรแยกจากลิงก์ชื่อรายการโดยตั้งใจ — คลิกชื่อแม่ยังต้องพาไปหน้าของ
            แม่เหมือนเดิม มีแต่ลูกศรเท่านั้นที่กาง/พับ (WO-P2-M4-009) เรนเดอร์เฉพาะตอน
            sidebar ทั้งแถบกางอยู่: โหมดย่อ (ไอคอนล้วน 56px) ไม่มีที่ว่างให้ปุ่มที่สอง
            อยู่แล้ว — ถ้าอยู่ในหน้าลูกตอน sidebar ถูกย่อ ลูกยังกางอัตโนมัติได้ตามปกติ
            เพียงแต่กดพับ/กางเองจากโหมดย่อไม่ได้จนกว่าจะขยาย sidebar ก่อน
          */}
          {hasChildren && expanded ? (
            <button
              type="button"
              className="cap-nav__toggle"
              aria-expanded={childrenExpanded}
              aria-controls={childrenId}
              aria-label={`${childrenExpanded ? 'ย่อ' : 'ขยาย'}เมนูย่อยของ ${item.label}`}
              onClick={() => onToggleChildren(item.id, !childrenExpanded)}
            >
              <span aria-hidden="true">{childrenExpanded ? '▾' : '▸'}</span>
            </button>
          ) : null}
        </div>
        {/*
          D-116 ข้อ 4 — ชั้นที่สามของเมนู: dropdown ลูกที่ path เดียวกับแม่ ต่าง
          search param เดียว ลูกได้สิทธิ์จากแม่เสมอ (ไม่ผ่าน `isNavItemVisible`
          ของตัวเอง) จึงเรนเดอร์ได้ทันทีที่แม่แสดงอยู่ — **แต่พับไว้เป็นค่าเริ่มต้น**
          (WO-P2-M4-009) ไม่เรนเดอร์เลยจนกว่าจะกาง เพื่อไม่ให้ต้องซ่อนด้วย CSS เฉย ๆ
        */}
        {hasChildren && childrenExpanded ? (
          <ul id={childrenId} className="cap-nav__children" aria-label={`เมนูย่อยของ ${item.label}`}>
            {visibleChildren.map((child) => (
              <NavChildEntry
                key={child.id}
                child={child}
                expanded={expanded}
                pathname={location.pathname}
                search={location.search}
              />
            ))}
          </ul>
        ) : null}
      </li>
    );
  }

  // รายการที่ยังทำไม่ได้จริงต้อง disabled จริง ไม่ใช่ลิงก์ที่กดแล้วเงียบ
  return (
    <li>
      <button
        type="button"
        disabled
        className="cap-nav__item cap-nav__item--disabled"
        title={itemTitle(item)}
        data-availability={item.availability}
      >
        <span className="cap-nav__icon" aria-hidden="true">
          {item.icon}
        </span>
        {expanded ? (
          <>
            <span className="cap-nav__label">{item.label}</span>
            <span className="cap-nav__flag">{availabilityLabel(item.availability)}</span>
          </>
        ) : (
          <span className="cap-visually-hidden">
            {item.label} — {availabilityLabel(item.availability)}
          </span>
        )}
      </button>
    </li>
  );
}

export function Sidebar({ expanded, groups = NAV_GROUPS, onToggle }: SidebarProps) {
  const [expandedChildren, setExpandedChildren] = useState<Record<string, boolean>>(() =>
    readStoredExpandedChildren(),
  );

  const toggleChildren = useCallback((itemId: string, nextValue: boolean) => {
    setExpandedChildren((previous) => {
      const next = { ...previous, [itemId]: nextValue };
      writeStoredExpandedChildren(next);
      return next;
    });
  }, []);

  return (
    <aside
      id="cap-desktop-sidebar"
      data-testid="desktop-sidebar"
      data-expanded={expanded ? 'true' : 'false'}
      className={`cap-sidebar${expanded ? '' : ' cap-sidebar--collapsed'}`}
      style={{ width: sidebarWidth(expanded) }}
    >
      <div className="cap-sidebar__brand">
        <img
          className="cap-sidebar__mark"
          src="/icons/cap-icon-192.png"
          width={30}
          height={30}
          alt="CAP ERP"
        />
        {expanded ? (
          <span className="cap-sidebar__identity">
            <span className="cap-sidebar__org">CAP ERP</span>
            {/*
              `WO-P2-M11-004` (D-107 ข้อ 8) — เดิมแสดง "เฟส 1 · vertical slice" ซึ่ง
              หยุดเป็นจริงในวันที่เฟสเปลี่ยน เวอร์ชันไม่หยุด — `version.ts` เป็นไฟล์
              generate จาก `scripts/run-version.mjs` (ห้ามแก้ด้วยมือ, นอกขอบเขตของ
              Work Order นี้)
            */}
            <span className="cap-sidebar__plant">{SYSTEM_VERSION_LABEL}</span>
          </span>
        ) : null}
      </div>

      <nav className="cap-sidebar__nav" aria-label="เมนูหลัก">
        {/*
          ไม่มีกลุ่มเหลือเลย = ยังไม่ยืนยันสิทธิ์ หรือสิทธิ์ชุดนี้ไม่มีเมนูใดเลย
          ต้องบอกตรง ๆ ไม่ใช่ปล่อยแถบว่างที่ดูเหมือนหน้าจอโหลดไม่ขึ้น
        */}
        {groups.length === 0 ? (
          <p
            className={`cap-nav__empty${expanded ? '' : ' cap-visually-hidden'}`}
            data-testid="nav-empty"
          >
            ไม่มีเมนูที่คุณมีสิทธิ์เข้าถึง
          </p>
        ) : null}
        {groups.map((group) => (
          <div key={group.id} className="cap-nav__group" data-group-id={group.id}>
            {expanded ? (
              <div className="cap-nav__group-title">{group.title}</div>
            ) : (
              <div className="cap-nav__group-rule" aria-hidden="true" />
            )}
            <ul className="cap-nav__list" aria-label={group.title}>
              {group.items
                .filter((item) => !item.hidden)
                .map((item) => (
                  <NavEntry
                    key={item.id}
                    item={item}
                    expanded={expanded}
                    expandedChildren={expandedChildren}
                    onToggleChildren={toggleChildren}
                  />
                ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="cap-sidebar__footer">
        <button
          type="button"
          className="cap-sidebar__toggle"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls="cap-desktop-sidebar"
        >
          <span aria-hidden="true">{expanded ? '«' : '»'}</span>
          {expanded ? <span>ย่อเมนู</span> : null}
          {!expanded ? <span className="cap-visually-hidden">ขยายเมนู</span> : null}
        </button>
      </div>
    </aside>
  );
}
